import DeleteForever from '@mui/icons-material/DeleteForever'
import MinorCrashIcon from '@mui/icons-material/MinorCrash'
import Tooltip from '@mui/material/Tooltip'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import { LabelField } from 'components'
import * as controllers from 'controllers'
import { InboundTripDto, InboundTripPAPSDto, VehicleDto, VehicleDocumentDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import { enqueueSnackbar } from 'notistack'
import { InboundTripPAPSVehiclesParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { formatCurrency, useDebounce } from 'utils'
import ManageVehiclesAddVehiclesDialog from './ManageVehiclesAddVehiclesDialog'
import { useLayout } from 'context'
import { VehicleDocumentsParameters } from 'parameters'

interface ManageVehiclesListCardProps {
  inboundTrip: InboundTripDto
  inboundTripPAPS: InboundTripPAPSDto
  setInboundTripPAPS: React.Dispatch<React.SetStateAction<InboundTripPAPSDto>>
}

export default function ManageVehiclesListCard({
  inboundTrip,
  inboundTripPAPS,
  setInboundTripPAPS
}: ManageVehiclesListCardProps) {
  const layout = useLayout()

  const [vehicleDocumentsMap, setVehicleDocumentsMap] = useState<{
    [vehicleGuid: string]: VehicleDocumentDto[]
  }>({})

  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicles: false,
    isAddingVehiclesToInboundTripPAPS: false,
    isRemovingVehiclesFromInboundTripPAPS: false
  })

  const [ManageVehiclesAddVehiclesDialogOpen, setManageVehiclesAddVehiclesDialogOpen] = useState<boolean>(false)
  const [parameters, setParameters] = useState<InboundTripPAPSVehiclesParameters>({
    page: 0,
    pageSize: 10,
    order: 'asc',
    orderBy: 'exporter'
  })
  const [selectedVehicles, setSelectedVehicles] = useState<VehicleDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>([])

  const getInboundTripPAPSVehicles = useDebounce(
    (inboundTripGuid: string, inboundTripPAPSGuid: string, parameters: InboundTripPAPSVehiclesParameters) => {
      setLoadingState('isGettingVehicles', true)
      controllers
        .getInboundTripPAPSVehicles(inboundTripGuid, inboundTripPAPSGuid, parameters)
        .then(response => {
          setVehicles(response.value)
          setTotalCount(response.totalCount ?? 0)
        })
        .finally(() => {
          setLoadingState('isGettingVehicles', false)
        })
    },
    DEBOUNCE_DELAY
  )

  useEffect(() => {
    if (inboundTrip && inboundTrip.inboundTripGuid && inboundTripPAPS && inboundTripPAPS.inboundTripPAPSGuid) {
      getInboundTripPAPSVehicles(inboundTrip.inboundTripGuid, inboundTripPAPS.inboundTripPAPSGuid, parameters)
    }
  }, [getInboundTripPAPSVehicles, inboundTrip, inboundTripPAPS, parameters])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isRemovingVehiclesFromInboundTripPAPS)
  }, [loadingState.isRemovingVehiclesFromInboundTripPAPS])

  useEffect(() => {
    const fetchDocumentsForVehicles = async () => {
      try {
        const newVehicleDocumentsMap: { [key: string]: VehicleDocumentDto[] } = {}

        for (const vehicle of vehicles) {
          const parameters: VehicleDocumentsParameters = {
            vehicleDocumentType: '',
            documentFileName: '',
            createdTimeDate: '',
            createdBy: '',
            page: 0,
            pageSize: 10,
            sortBy: ''
          }

          const response = await controllers.getVehicleDocuments(vehicle.vehicleGuid ?? '', parameters)

          newVehicleDocumentsMap[vehicle.vehicleGuid ?? ''] = response.value ?? []
        }

        // Update the state with the fetched documents
        setVehicleDocumentsMap(newVehicleDocumentsMap)
      } catch (error) {
        console.error('Error fetching vehicle documents:', error)
      }
    }

    if (vehicles.length > 0) {
      fetchDocumentsForVehicles()
    }
  }, [vehicles]) // Re-run when the vehicles array changes

  return (
    <>
      <ManageVehiclesAddVehiclesDialog
        isLoading={loadingState.isAddingVehiclesToInboundTripPAPS}
        inboundTripPAPS={inboundTripPAPS}
        onClose={() => {
          setManageVehiclesAddVehiclesDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isAddingVehiclesToInboundTripPAPS', true)
          controllers
            .addVehiclesToInboundTripPAPS(inboundTrip.inboundTripGuid!, inboundTripPAPS.inboundTripPAPSGuid!, values)
            .then(response => {
              setManageVehiclesAddVehiclesDialogOpen(false)
              enqueueSnackbar('Vehicles Added Successfully!', { variant: 'success' })
              setInboundTripPAPS(response.value)
              // getInboundTripPAPSVehicles(inboundTrip.inboundTripGuid, inboundTripPAPS.inboundTripPAPSGuid, parameters)
            })
            .finally(() => {
              setLoadingState('isAddingVehiclesToInboundTripPAPS', false)
            })
        }}
        open={ManageVehiclesAddVehiclesDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Manage Vehicles
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto' container spacing={1} alignItems='center'>
                <Grid item>
                  <Button
                    color='error'
                    disabled={inboundTripPAPS.isLocked || inboundTrip.isLocked || selectedVehicles.length === 0}
                    endIcon={<DeleteForever />}
                    onClick={() => {
                      setLoadingState('isRemovingVehiclesFromInboundTripPAPS', true)
                      controllers
                        .removeVehiclesFromInboundTripPAPS(
                          inboundTrip.inboundTripGuid!,
                          inboundTripPAPS.inboundTripPAPSGuid!,
                          selectedVehicles
                        )
                        .then(response => {
                          setSelectedVehicles([])
                          enqueueSnackbar('Vehicles Removed Successfully!', { variant: 'success' })
                          setInboundTripPAPS(response.value)
                          // getInboundTripPAPSVehicles(
                          //   inboundTrip.inboundTripGuid!,
                          //   inboundTripPAPS.inboundTripPAPSGuid!,
                          //   parameters
                          // )
                        })
                        .finally(() => {
                          setLoadingState('isRemovingVehiclesFromInboundTripPAPS', false)
                        })
                    }}
                    variant='text'
                  >
                    REMOVE SELECTED
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    color='primary'
                    disabled={inboundTripPAPS.isLocked || inboundTrip.isLocked}
                    endIcon={<MinorCrashIcon />}
                    onClick={() => {
                      setManageVehiclesAddVehiclesDialogOpen(true)
                    }}
                    variant='text'
                  >
                    ADD VEHICLES
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={3}>
              <LabelField label='# of Vehicles' value={inboundTripPAPS.numberOfVehiclesInPAPS} />
            </Grid>

            <Grid item xs={3}>
              <LabelField
                label='Invoice Total'
                value={inboundTripPAPS.invoiceTotal ? formatCurrency(inboundTripPAPS.invoiceTotal) : ''}
              />
            </Grid>

            <Grid item xs={3}>
              <LabelField label='Total Weight (Lbs)' value={inboundTripPAPS.totalWeightLbs?.toFixed(2)} />
            </Grid>

            <Grid item xs={3}>
              <LabelField label='Gross Weight (Kg)' value={inboundTripPAPS.grossWeightKg?.toFixed(2)} />
            </Grid>

            {loadingState.isGettingVehicles && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!loadingState.isGettingVehicles && (
              <Grid item xs={12}>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            checked={selectedVehicles.length === totalCount && selectedVehicles.length !== 0}
                            disabled={inboundTripPAPS.isLocked || selectedVehicles.length === 0}
                            indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== totalCount}
                            onChange={() => {
                              if (selectedVehicles.length === totalCount) {
                                setSelectedVehicles([])
                              } else {
                                setSelectedVehicles(vehicles)
                              }
                            }}
                          />
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'exporter' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'exporter'}
                            direction={parameters.orderBy === 'exporter' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'exporter' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'exporter'
                              })
                            }}
                          >
                            Exporter
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'buyer' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'buyer'}
                            direction={parameters.orderBy === 'buyer' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'buyer' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'buyer'
                              })
                            }}
                          >
                            Buyer
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'vin' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'vin'}
                            direction={parameters.orderBy === 'vin' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'vin' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'vin'
                              })
                            }}
                          >
                            VIN
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'year' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'year'}
                            direction={parameters.orderBy === 'year' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'year' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'year'
                              })
                            }}
                          >
                            Year
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'make' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'make'}
                            direction={parameters.orderBy === 'make' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'make' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'make'
                              })
                            }}
                          >
                            Make
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'model' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'model'}
                            direction={parameters.orderBy === 'model' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'model' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'model'
                              })
                            }}
                          >
                            Model
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'amountUSD' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'amountUSD'}
                            direction={parameters.orderBy === 'amountUSD' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'amountUSD' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'amountUSD'
                              })
                            }}
                          >
                            Amount (USD)
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'curbWeight' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'curbWeight'}
                            direction={parameters.orderBy === 'curbWeight' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'curbWeight' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'curbWeight'
                              })
                            }}
                          >
                            Curb Weight
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'weightGVWR' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'weightGVWR'}
                            direction={parameters.orderBy === 'weightGVWR' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'weightGVWR' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'weightGVWR'
                              })
                            }}
                          >
                            GVWR
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'hs7' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'hs7'}
                            direction={parameters.orderBy === 'hs7' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'hs7' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'hs7'
                              })
                            }}
                          >
                            HS-7
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'epa3520' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'epa3520'}
                            direction={parameters.orderBy === 'epa3520' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'epa3520' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'epa3520'
                              })
                            }}
                          >
                            EPA 3520
                          </TableSortLabel>
                        </TableCell>

                        <TableCell
                          sx={theme => ({ color: theme.palette.primary.main })}
                          sortDirection={parameters.orderBy === 'bond' ? parameters.order : false}
                        >
                          <TableSortLabel
                            active={parameters.orderBy === 'bond'}
                            direction={parameters.orderBy === 'bond' ? parameters.order : undefined}
                            onClick={() => {
                              setParameters({
                                ...parameters,
                                order: parameters.orderBy === 'bond' && parameters.order === 'asc' ? 'desc' : 'asc',
                                orderBy: 'bond'
                              })
                            }}
                          >
                            Bond
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {vehicles?.map((vehicle, index) => {
                        const vehicleDocuments = vehicleDocumentsMap[vehicle.vehicleGuid ?? ''] || []

                        const hs7Document = vehicleDocuments
                          .filter(d => d.displayFileName?.toLowerCase().includes('hs-7'))
                          .reverse()[0]

                        const epaDocument = vehicleDocuments
                          .filter(d => d.displayFileName?.toLowerCase().includes('epa-3520'))
                          .reverse()[0]

                        const bondDocument = vehicleDocuments
                          .filter(d => d.displayFileName?.toLowerCase().includes('bond'))
                          .reverse()[0]

                        return (
                          <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                            <TableCell padding='checkbox'>
                              <Checkbox
                                color='primary'
                                checked={selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                                disabled={inboundTripPAPS.isLocked}
                                onChange={() => {
                                  if (selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)) {
                                    setSelectedVehicles(selectedVehicles.filter(v => v.vehicleGuid !== vehicle.vehicleGuid))
                                  } else {
                                    setSelectedVehicles([...selectedVehicles, vehicle])
                                  }
                                }}
                              />
                            </TableCell>

                            <TableCell>{vehicle.exporterCompany?.name}</TableCell>

                            <TableCell>{vehicle.buyerCompany?.name}</TableCell>

                            <TableCell>{vehicle.vin}</TableCell>

                            <TableCell>{vehicle.year}</TableCell>

                            <TableCell>{vehicle.make}</TableCell>

                            <TableCell>{vehicle.model}</TableCell>

                            <TableCell>{vehicle.amountUSD ? formatCurrency(vehicle.amountUSD) : ''}</TableCell>

                            <TableCell>{vehicle.curbWeight}</TableCell>

                            <TableCell>{vehicle.weightGVWR}</TableCell>

                            <TableCell>
                              {hs7Document ? (
                                <Tooltip title={hs7Document.displayFileName}>
                                  <Typography component='span' sx={{ fontSize: '0.75rem !important' }}>
                                    {hs7Document.displayFileName!}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography component='span' sx={{ fontSize: '0.75rem !important' }}>
                                  'No HS-7 Document'
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              {epaDocument ? (
                                <Tooltip title={epaDocument.displayFileName}>
                                  <Typography component='span' sx={{ fontSize: '0.75rem !important' }}>
                                    {epaDocument.displayFileName!}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography component='span' sx={{ fontSize: '0.75rem !important' }}>
                                  'No EPA-3520 Document'
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ maxWidth: 200, wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {bondDocument ? (
                                <Tooltip title={bondDocument.displayFileName}>
                                  <Typography component='span' sx={{ fontSize: '0.75rem !important' }}>
                                    {bondDocument.displayFileName!}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography component='span' sx={{ fontSize: '0.75rem !important' }}>
                                  'No Bond Document'
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25]}
                          SelectProps={{ fullWidth: false }}
                          count={totalCount}
                          rowsPerPage={parameters.pageSize}
                          page={parameters.page}
                          onPageChange={(_, page) => {
                            setParameters({
                              ...parameters,
                              page
                            })
                          }}
                          onRowsPerPageChange={e => {
                            setParameters({
                              ...parameters,
                              page: 0,
                              pageSize: e.target.value as unknown as number
                            })
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
